import { SwapInstructionsResponse } from "@loopscale/pandora-types";
import { Result, deserializeBase64VersionedTransaction } from "@bridgesplit/utils";
import { VersionedTransaction } from "@solana/web3.js";

import { AbfGeneratorResult, AbfTransactionDetails, TransactionSenderOptions } from "../types";
import { useCreateSimulationPromise } from "./simulation";
import { useUserTokens } from "../api";

export interface TokenSwapTransactionParams {
    swapQuote: SwapInstructionsResponse;
    userWallet: string;
}

/**
 * Build a VersionedTransaction from Pandora swap instructions
 */
function buildTokenSwapTransactions(swapQuote: SwapInstructionsResponse): VersionedTransaction[] {
    const steps = swapQuote.swapInstructions ?? [];
    if (!steps.length) throw new Error("No swap instructions");

    const swapTransactions: VersionedTransaction[] = [];
    for (const step of steps) {
        if (step.txBase64) {
            swapTransactions.push(deserializeBase64VersionedTransaction(step.txBase64));
        }
    }

    if (!swapTransactions.length) throw new Error("No transaction base64 in swap instructions");
    return swapTransactions;
}

export function useTokenSwapTransaction(): AbfTransactionDetails<TokenSwapTransactionParams> {
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParams(params: TokenSwapTransactionParams): AbfGeneratorResult {
        try {
            const swapTransactions = buildTokenSwapTransactions(params.swapQuote);

            const orderedTransactions = [
                {
                    transactions: swapTransactions.map((transaction, i) => ({
                        transaction,
                        identifier: swapTransactions.length === 1 ? "Swap Tokens" : `Swap step ${i + 1}`
                    }))
                }
            ];
            return Result.ok({
                transactions: orderedTransactions,
                simulationPromise: createSimulationPromise(orderedTransactions)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        delayMethod: "sleep",
        refetch: () => {
            refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Swapping Tokens" };
}
